"use client";

import { useMutation } from "@apollo/client";
import { CheckCircle, Clock, Globe, Loader2, MapPin, Phone } from "lucide-react";
import React, { useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { BotpressChat } from "@/components/botpress/botpress-chat";
import { SocialLinks, WhatsAppIcon } from "@/components/footer/social-links";
import { Checkbox } from "@/components/rac/checkbox";
import { CREATE_REQUEST } from "@/gql/request";
import { COMPANY_INFO } from "@/info/company";
import { Country, getCountriesWithPhoneCodes, getDefaultCountry } from "@/info/countries";
import { formatPhoneNumber, isValidPhoneNumber } from "@/info/login";
import { trackContactCall, trackContactMail } from "@/utils/analytics/track-contact";
import { trackEvent } from "@/utils/analytics/track-event";
import { logger } from "@/utils/logger";
interface FormData {
  name: string;
  phone: string;
  email: string;
  city: string;
  message: string;
  rcsConsent: boolean;
}
export function ContactPageClient() {
  // Memoize country list to prevent re-computation on every render
  const countries = useMemo(() => getCountriesWithPhoneCodes(), []);

  // Form State
  const [selectedCountry, setSelectedCountry] = useState<Country>(getDefaultCountry());
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [createRequest, {
    loading
  }] = useMutation(CREATE_REQUEST);

  // React Hook Form
  const {
    register,
    handleSubmit,
    formState: {
      errors
    },
    watch,
    setValue
  } = useForm<FormData>({
    defaultValues: {
      name: "",
      phone: "",
      email: "",
      city: "",
      message: "",
      rcsConsent: false
    },
    mode: "onBlur"
  });
  const formData = watch();

  // Register rcsConsent manually with validation
  React.useEffect(() => {
    register("rcsConsent", {
      required: "Consent is required",
      validate: value => value === true || "Consent is required"
    });
  }, [register]);

  // Sanitize URL to remove sensitive query parameters
  const getSanitizedUrl = (): string | undefined => {
    if (typeof window === "undefined") return undefined;
    try {
      const url = new URL(window.location.href);
      // Remove potentially sensitive query parameters
      const sensitiveParams = ["token", "auth", "key", "api_key", "apikey", "access_token", "refresh_token", "session", "sid", "password", "pwd", "secret", "utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content", "fbclid", "gclid", "msclkid", "mc_eid", "mc_cid"];
      sensitiveParams.forEach(param => url.searchParams.delete(param));

      // Return sanitized URL (origin + pathname only, no query params for safety)
      return `${url.origin}${url.pathname}`;
    } catch {
      return undefined;
    }
  };
  const onSubmit = handleSubmit(async (data: FormData) => {
    try {
      const fullPhone = formatPhoneNumber(data.phone, selectedCountry.code);
      const {
        data: gqlData,
        errors: gqlErrors
      } = await createRequest({
        variables: {
          params: {
            createRequest: {
              type: "callback",
              name: data.name.trim(),
              phone: fullPhone,
              email: data.email.trim(),
              city: data.city.trim(),
              message: data.message.trim(),
              rcsConsent: data.rcsConsent,
              url: getSanitizedUrl()
            }
          }
        }
      });
      if (gqlData?.request?.createRequest?.success) {
        setIsSubmitted(true);
        trackEvent({
          google: {
            event: "generate_lead",
            options: {
              source: "contact_form"
            }
          },
          matomo: {
            event: "contact-form-submit",
            options: {
              category: "contact"
            }
          }
        });
        toast.success("Request Submitted!");
      } else {
        const msg = gqlErrors?.[0]?.message || gqlData?.request?.createRequest?.message || "Failed to submit";
        // Log error without sensitive PII data
        logger.error("Contact form failed", {
          success: gqlData?.request?.createRequest?.success,
          hasErrors: !!gqlErrors,
          errorCount: gqlErrors?.length,
          errorMessage: gqlErrors?.[0]?.message
        });
        toast.error(msg);
      }
    } catch (error) {
      // Log error without sensitive PII data
      logger.error("Contact form error:", {
        errorType: error instanceof Error ? error.name : typeof error,
        errorMessage: error instanceof Error ? error.message : "Unknown error"
      });
      toast.error("Something went wrong");
    }
  });
  const handleQuickContact = (type: "phone" | "email") => {
    if (type === "phone") trackContactCall();else trackContactMail();
    trackEvent({
      google: {
        event: "contact_method_clicked",
        options: {
          method: type
        }
      },
      matomo: {
        event: "contact-method-click",
        options: {
          category: "contact"
        }
      }
    });
  };
  const handleWhatsAppClick = () => {
    trackEvent({
      facebook: {
        event: "Contact",
        options: {
          content_name: "WhatsApp Contact",
          content_category: "whatsapp"
        }
      },
      google: {
        event: "select_content",
        options: {
          content_type: "whatsapp",
          item_id: "whatsapp_contact"
        }
      },
      matomo: {
        event: "click on whatsapp",
        options: {
          category: "contact"
        }
      }
    });
  };
  const handleSocialClick = (platform: string, url_0: string) => {
    trackEvent({
      facebook: {
        event: "Contact",
        options: {
          content_name: `Social Media - ${platform}`,
          content_category: "social_media"
        }
      },
      google: {
        event: "select_content",
        options: {
          content_type: "social_media",
          item_id: platform.toLowerCase(),
          custom_parameters: {
            destination_url: url_0
          }
        }
      },
      matomo: {
        event: `click on ${platform.toLowerCase()}`,
        options: {
          category: "contact"
        }
      }
    });
  };

  // Dynamic Styles
  const glassClass = "bg-white/40 backdrop-blur-xl border border-white/40 shadow-xl shadow-slate-200/20";
  const inputClass = "w-full bg-white/50 border-white/50 rounded-xl px-5 py-4 focus:ring-primary focus:border-primary text-slate-900 placeholder-slate-400 transition-all outline-none";
  return <div className="min-h-screen relative overflow-x-hidden bg-neutral-50 text-slate-800 font-sans selection:bg-primary selection:text-white">
			{/* Organic Shapes Background */}
			<div className="fixed inset-0 pointer-events-none overflow-hidden">
				<div className="absolute -top-20 -right-20 w-[500px] h-[500px] bg-primary/5 rounded-full blur-[80px] z-0"></div>
				<div className="absolute top-1/2 -left-20 w-[400px] h-[400px] bg-primary/10 rounded-full blur-[80px] z-0"></div>
			</div>

			<main className="relative z-10 max-w-5xl mx-auto px-6 pt-16 pb-24 text-center">
				{/* Hero Header */}
				<h1 className="font-display text-5xl md:text-7xl mb-16 tracking-tight text-slate-900 animate-fade-in-down">
					Get in Touch with <span className="italic font-normal text-primary">Settlin</span>
				</h1>

				{/* SECTION 1: TOP PRIORITY - Quick Actions & Live Chat */}
				<section className="animate-fade-in-up delay-100 mb-12">
					<div className="grid md:grid-cols-2 gap-8">
						{/* Connect With Us / Live Chat */}
						<div className={`${glassClass} p-8 rounded-3xl text-left flex flex-col justify-between`}>
							<div>
								<h3 className="text-2xl font-bold mb-4 text-slate-900">Connect With Us</h3>
								<div className="flex gap-4 mb-8">
									<SocialLinks handleSocialClick={handleSocialClick} linkClassName="text-slate-400 hover:text-primary transition-colors" />
								</div>
								<p className="text-sm text-slate-500 mb-3">Need immediate help?</p>
							</div>
							<div className="mt-auto">
								<BotpressChat mode="custom" className="w-full bg-gradient-to-r from-red-600 to-blue-600 hover:opacity-90 text-white font-semibold py-4 px-6 rounded-2xl transition-all shadow-lg flex items-center justify-center gap-3 text-lg group cursor-pointer">
									Start Live Chat
								</BotpressChat>
							</div>
						</div>

						{/* Quick Call / WhatsApp */}
						<div className={`${glassClass} p-8 rounded-3xl flex flex-col justify-center gap-6`}>
							<a href={`tel:${COMPANY_INFO.contact.phone.primary.replace(/\s|-/g, "")}`} onClick={() => handleQuickContact("phone")} className="flex items-center gap-6 p-2 rounded-full hover:bg-white/40 transition-colors group">
								<div className="w-16 h-16 rounded-full border-2 border-primary/20 text-primary flex items-center justify-center bg-white shadow-sm group-hover:scale-110 transition-transform">
									<Phone className="w-8 h-8" />
								</div>
								<span className="text-2xl font-medium text-slate-700">Call Now</span>
							</a>

							<a href="https://api.whatsapp.com/send?phone=919590039003" target="_blank" onClick={handleWhatsAppClick} rel="noopener noreferrer" className="flex items-center gap-6 p-2 rounded-full hover:bg-white/40 transition-colors group">
								<div className="w-16 h-16 rounded-full border-2 border-primary/20 text-primary flex items-center justify-center bg-white shadow-sm group-hover:scale-110 transition-transform">
									<WhatsAppIcon className="w-8 h-8" />
								</div>
								<span className="text-2xl font-medium text-slate-700">WhatsApp</span>
							</a>
						</div>
					</div>
				</section>

				{/* SECTION 2: OUR OFFICE (High Priority) */}
				<section className="animate-fade-in-up delay-200 mb-8">
					<div className={`${glassClass} p-8 md:p-10 rounded-3xl text-left`}>
						<div className="flex flex-col md:flex-row gap-8 items-start md:items-center justify-between">
							<div className="flex items-start gap-4">
								<div className="w-12 h-12 rounded-full bg-red-100 text-primary flex items-center justify-center flex-shrink-0">
									<MapPin className="w-6 h-6" />
								</div>
								<div>
									<h3 className="text-xl font-bold mb-2">Our Office</h3>
									<p className="text-slate-600 leading-relaxed text-lg">
										{COMPANY_INFO.legalName}
										<br />
										{COMPANY_INFO.address.structured.streetAddress}, {COMPANY_INFO.address.structured.addressLocality}
										<br />
										{COMPANY_INFO.address.structured.city} - {COMPANY_INFO.address.structured.postalCode}
									</p>
								</div>
							</div>
							<a href={COMPANY_INFO.address.googleMapsUrl} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-primary font-semibold hover:underline">
								<Globe className="w-5 h-5" />
								View on Google Maps
							</a>
						</div>
					</div>
				</section>

				{/* SECTION 3: BUSINESS HOURS & SERVICES (Medium Priority) */}
				<section className="animate-fade-in-up delay-300 mb-12">
					<div className="grid md:grid-cols-3 gap-6">
						{/* Business Hours */}
						<div className={`${glassClass} p-8 rounded-3xl text-left md:col-span-1`}>
							<div className="flex items-center gap-3 mb-6">
								<Clock className="w-6 h-6 text-orange-500" />
								<h3 className="text-xl font-bold">Business Hours</h3>
							</div>
							<div className="space-y-4">
								<div>
									<p className="text-slate-500 text-sm mb-1">Monday - Sunday</p>
									<p className="text-lg font-semibold">10:00 AM - 6:00 PM</p>
								</div>
								<p className="text-slate-600 text-sm italic">We&apos;re available 7 days a week to assist you.</p>
							</div>
						</div>

						{/* Our Services */}
						<div className={`${glassClass} p-8 rounded-3xl text-left md:col-span-2`}>
							<div className="flex items-center gap-3 mb-6">
								<CheckCircle className="w-6 h-6 text-green-500" />
								<h3 className="text-xl font-bold">Our Services</h3>
							</div>
							<div className="grid md:grid-cols-2 gap-4">
								{COMPANY_INFO.services.map(service => <div key={service.name} className="flex gap-3">
										<div className="w-1.5 h-1.5 rounded-full bg-primary mt-2 flex-shrink-0" />
										<div>
											<p className="font-semibold text-slate-800">{service.name}</p>
											<p className="text-sm text-slate-500 leading-snug">{service.description}</p>
										</div>
									</div>)}
							</div>
						</div>
					</div>
				</section>

				{/* SECTION 4: SOCIAL PROOF / STATS (Good to have) */}
				<section className="animate-fade-in-up delay-400 mb-16">
					<div className="grid grid-cols-1 md:grid-cols-3 gap-6">
						<div className={`${glassClass} p-6 rounded-2xl flex flex-col items-center justify-center`}>
							<div className="text-3xl font-bold text-slate-900 mb-1">{COMPANY_INFO.rating.value}/5</div>
							<div className="text-xs uppercase tracking-widest text-slate-500">Customer Rating</div>
						</div>
						<div className={`${glassClass} p-6 rounded-2xl flex flex-col items-center justify-center`}>
							<div className="text-3xl font-bold text-slate-900 mb-1">8,000+</div>
							<div className="text-xs uppercase tracking-widest text-slate-500">Happy Customers</div>
						</div>
						<div className={`${glassClass} p-6 rounded-2xl flex flex-col items-center justify-center`}>
							<div className="text-3xl font-bold text-slate-900 mb-1">
								Since {new Date(COMPANY_INFO.foundingDate).getFullYear()}
							</div>
							<div className="text-xs uppercase tracking-widest text-slate-500">Years of Excellence</div>
						</div>
					</div>
				</section>

				{/* SECTION 5: CONTACT FORM (Last - "Just for RCS") */}
				<section className="animate-fade-in-up delay-500 max-w-3xl mx-auto">
					<div className={`${glassClass} p-8 md:p-12 rounded-3xl border-t-4 border-t-primary/50`}>
						{isSubmitted ? <div className="text-center py-12">
								<div className="w-20 h-20 bg-green-500/10 rounded-full flex items-center justify-center mx-auto mb-6 animate-bounce">
									<CheckCircle className="w-10 h-10 text-green-600" />
								</div>
								<h3 className="text-3xl font-bold text-slate-900 mb-4">Request Sent!</h3>
								<p className="text-slate-600 mb-8 max-w-md mx-auto">
									Thank you for reaching out. Our team will get back to you shortly at {formData.phone}.
								</p>
								<button onClick={() => setIsSubmitted(false)} className="px-8 py-3 rounded-xl bg-white/50 hover:bg-white/70 border border-slate-200 font-medium transition-colors">
									Send Another Request
								</button>
							</div> : <form onSubmit={onSubmit} className="space-y-8">
								<div className="text-left mb-6">
									<h2 className="text-3xl font-bold mb-2">Send us a Message</h2>
									<p className="text-slate-500">We&apos;d love to hear from you. Fill out the form below.</p>
								</div>

								<div className="grid grid-cols-1 md:grid-cols-2 gap-8">
									<div className="text-left space-y-3">
										<label className="block text-xs uppercase tracking-widest text-slate-500 font-bold ml-1">
											Full Name <span className="text-red-500">*</span>
										</label>
										<input className={`${inputClass} font-medium`} placeholder="John Doe" type="text" {...register("name", {
                  required: "Full name is required",
                  validate: value_0 => value_0.trim() !== "" || "Full name is required"
                })} />
										{errors.name && <p className="text-red-500 text-xs ml-1">{errors.name.message}</p>}
									</div>

									<div className="text-left space-y-3">
										<label className="block text-xs uppercase tracking-widest text-slate-500 font-bold ml-1">
											Phone <span className="text-red-500">*</span>
										</label>
										<div className="flex gap-3">
											<div className="relative w-[35%] min-w-[90px]">
												<select className={`${inputClass} appearance-none pr-8 cursor-pointer text-sm`} value={selectedCountry.countryCode} onChange={e => {
                      const c_0 = countries.find(c => c.countryCode === e.target.value);
                      if (c_0) setSelectedCountry(c_0);
                    }}>
													{countries.map(c_1 => <option key={c_1.countryCode} value={c_1.countryCode}>
															{c_1.flag} {c_1.code}
														</option>)}
												</select>
											</div>
											<input className={`${inputClass} flex-1 font-medium`} placeholder="98765 43210" type="tel" inputMode="numeric" {...register("phone", {
                    required: "Phone number is required",
                    validate: value_1 => {
                      if (!value_1.trim()) return "Phone number is required";
                      if (!isValidPhoneNumber(value_1, selectedCountry.code)) return "Invalid phone number";
                      return true;
                    },
                    onChange: e_0 => {
                      const value_2 = e_0.target.value.replace(/[^\d]/g, "");
                      setValue("phone", value_2);
                    }
                  })} />
										</div>
										{errors.phone && <p className="text-red-500 text-xs ml-1">{errors.phone.message}</p>}
									</div>
								</div>

								<div className="grid grid-cols-1 md:grid-cols-2 gap-8">
									<div className="text-left space-y-3">
										<label className="block text-xs uppercase tracking-widest text-slate-500 font-bold ml-1">
											Email Address <span className="text-red-500">*</span>
										</label>
										<input className={`${inputClass} font-medium`} placeholder="john@example.com" type="email" {...register("email", {
                  required: "Email is required",
                  validate: value_3 => {
                    if (!value_3.trim()) return "Email is required";
                    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value_3)) return "Invalid email address";
                    return true;
                  }
                })} />
										{errors.email && <p className="text-red-500 text-xs ml-1">{errors.email.message}</p>}
									</div>

									<div className="text-left space-y-3">
										<label className="block text-xs uppercase tracking-widest text-slate-500 font-bold ml-1">
											City <span className="text-red-500">*</span>
										</label>
										<input className={`${inputClass} font-medium`} placeholder="Bangalore" type="text" {...register("city", {
                  required: "City is required",
                  validate: value_4 => value_4.trim() !== "" || "City is required"
                })} />
										{errors.city && <p className="text-red-500 text-xs ml-1">{errors.city.message}</p>}
									</div>
								</div>

								<div className="text-left space-y-3">
									<label className="block text-xs uppercase tracking-widest text-slate-500 font-bold ml-1">
										Message
									</label>
									<textarea className={`${inputClass} resize-none h-40 font-medium`} placeholder="How can we help you today?" rows={4} {...register("message")}></textarea>
								</div>

								<div className={`text-left p-4 rounded-xl border transition-colors cursor-pointer ${errors.rcsConsent ? "border-red-200 bg-red-50/50" : "border-white/20 bg-white/20 hover:bg-white/30"}`} onClick={() => setValue("rcsConsent", !formData.rcsConsent, {
              shouldValidate: true
            })}>
									<div className="flex items-start gap-3">
										<Checkbox id="contact-rcs-consent" isSelected={formData.rcsConsent} onChange={checked => setValue("rcsConsent", checked, {
                  shouldValidate: true
                })} className="mt-0.5">
											<div className="text-xs text-slate-600 leading-relaxed pointer-events-none">
												I authorise detailed updates via SMS/WhatsApp/RCS. This overrides DNC registry.{" "}
												<span className="text-red-500 font-bold">*</span>
											</div>
										</Checkbox>
									</div>
									{errors.rcsConsent && <p className="text-red-500 text-xs mt-1.5 pl-7">{errors.rcsConsent.message}</p>}
								</div>

								<button className="w-full bg-primary hover:bg-primary-600 text-white font-bold py-5 rounded-xl transition-all shadow-lg shadow-primary/30 hover:shadow-xl hover:-translate-y-0.5 active:scale-[0.98] tracking-widest uppercase flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed" type="submit" disabled={loading}>
									{loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Submit Request"}
								</button>
							</form>}
					</div>
					<div className="mt-8 text-xs text-slate-400">RERA: {COMPANY_INFO.legal.reraId}</div>
				</section>
			</main>
		</div>;
}